/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState } from 'react';
import { Cloud, RefreshCw, Wifi, Check, X } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import type { VertexAnthropicOrgSettings } from '@shared/types/organizationSettings';

interface VertexConnectionStatus {
  tested: boolean;
  success: boolean;
  error?: string;
}

interface VertexAISectionProps {
  settings: VertexAnthropicOrgSettings;
  fallbackProjectId: string;
  onChange: (settings: VertexAnthropicOrgSettings) => void;
  onDirty: () => void;
}

export function VertexAISection({ 
  settings, 
  fallbackProjectId,
  onChange, 
  onDirty 
}: VertexAISectionProps) {
  const { toast } = useToast();
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<VertexConnectionStatus>({ 
    tested: false, 
    success: false 
  });

  const testConnection = async () => {
    const effectiveProjectId = settings.projectId || fallbackProjectId;
    
    if (!effectiveProjectId) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: 'Please provide a GCP Project ID (either in Vertex AI settings or shared GCP Project settings)'
      });
      return;
    }
    
    setIsTestingConnection(true);
    setConnectionStatus({ tested: false, success: false });
    
    try {
      const response = await fetch('/api/settings/vertex-anthropic/test-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: effectiveProjectId,
          region: settings.region || 'global'
        })
      });
      
      const result = await response.json();
      
      if (result.success && result.data?.success) {
        setConnectionStatus({ tested: true, success: true });
        toast({
          title: 'Connection successful',
          description: result.data.message || 'Successfully connected to Anthropic via Vertex AI',
        });
      } else {
        setConnectionStatus({
          tested: true,
          success: false,
          error: result.data?.message || result.error?.message || 'Connection failed'
        });
      }
    } catch (error: any) {
      setConnectionStatus({
        tested: true,
        success: false,
        error: error.message || 'Connection test failed'
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const effectiveProjectId = settings.projectId || fallbackProjectId;

  return (
    <div style={{ 
      marginBottom: '32px',
      padding: '20px',
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #ddd'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Cloud size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0 }}>Vertex AI for Anthropic</h3>
      </div>
      
      <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '16px' }}>
        When enabled, Anthropic models will be accessed via Google Cloud Vertex AI instead of the direct Anthropic API.
        This uses application-default credentials from your GCP environment.
      </p>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={settings.enabled}
            onChange={(e) => {
              onChange({ ...settings, enabled: e.target.checked });
              onDirty();
            }}
          />
          <span style={{ fontSize: '14px' }}>Enable Vertex AI for Anthropic models</span>
        </label>
      </div>
      
      {settings.enabled && (
        <div style={{ 
          padding: '16px', 
          background: '#fff',
          borderRadius: '6px',
          border: '1px solid #e5e7eb'
        }}>
          <div className="form-group">
            <label htmlFor="vertex-project-id">Google Cloud Project ID <span style={{ color: '#dc2626' }}>*</span></label>
            <input
              id="vertex-project-id"
              type="text"
              placeholder="Enter your GCP project ID"
              value={settings.projectId}
              onChange={(e) => {
                onChange({ ...settings, projectId: e.target.value });
                onDirty();
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              The GCP project with Vertex AI enabled (pre-populated from shared GCP settings if configured)
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="vertex-region">Vertex AI Region</label>
            <select
              id="vertex-region"
              value={settings.region}
              onChange={(e) => {
                onChange({ ...settings, region: e.target.value });
                onDirty();
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            >
              <option value="global">Global</option>
              <option value="us-east1">us-east1 (South Carolina)</option>
              <option value="us-east4">us-east4 (Virginia)</option>
              <option value="us-east5">us-east5 (Ohio)</option>
              <option value="us-central1">us-central1 (Iowa)</option>
              <option value="us-west1">us-west1 (Oregon)</option>
              <option value="us-west4">us-west4 (Nevada)</option>
              <option value="europe-west1">europe-west1 (Belgium)</option>
              <option value="europe-west4">europe-west4 (Netherlands)</option>
              <option value="asia-southeast1">asia-southeast1 (Singapore)</option>
            </select>
            <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
              The region where Vertex AI Claude models are deployed
            </small>
          </div>
          
          <div style={{ 
            marginTop: '12px',
            padding: '12px',
            background: '#f0f9ff',
            borderRadius: '4px',
            border: '1px solid #bae6fd',
            fontSize: '13px',
            color: '#0369a1'
          }}>
            <strong>Note:</strong> Ensure you have authenticated to GCP with application-default credentials:
            <br />
            <code style={{ 
              display: 'block', 
              marginTop: '8px', 
              padding: '8px', 
              background: '#e0f2fe', 
              borderRadius: '4px',
              fontFamily: 'monospace',
              fontSize: '12px'
            }}>
              gcloud auth application-default login
            </code>
          </div>
          
          {/* Test Connection Button */}
          <div style={{ marginTop: '16px' }}>
            <button
              className="btn"
              onClick={testConnection}
              disabled={isTestingConnection || !effectiveProjectId}
              style={{
                width: 'auto',
                padding: '8px 16px',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isTestingConnection ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  Testing Connection...
                </>
              ) : (
                <>
                  <Wifi size={16} />
                  Test Connection
                </>
              )}
            </button>
            
            {connectionStatus.tested && (
              <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center' }}>
                {connectionStatus.success ? (
                  <>
                    <Check size={16} style={{ color: '#28a745', marginRight: '8px' }} />
                    <span style={{
                      padding: '4px 12px',
                      background: '#d1fae5',
                      color: '#065f46',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontWeight: 500
                    }}>
                      Connected to Anthropic via Vertex AI
                    </span>
                  </>
                ) : (
                  <>
                    <X size={16} style={{ color: '#dc3545', marginRight: '8px' }} />
                    <span style={{
                      padding: '4px 12px',
                      background: '#fef2f2',
                      color: '#b91c1c',
                      borderRadius: '4px',
                      fontSize: '13px',
                      fontWeight: 500
                    }}>
                      {connectionStatus.error || 'Connection failed'}
                    </span>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
